import { MediaImageRepositoryProcessingDOM } from '@/scenes/engine/objects/media-repository/MediaImageRepositoryProcessingDOM'
import { BatchImage, NormalizedBBox, getResizeObjectFit } from '@/store/slices/batch/slice'
import { requestBgRemoveMask } from '@/utils/bgRemoveApi'
import { fileToDataUrl } from '@/utils/uploadFiles'
import { ShadowConfig } from '@/scenes/engine/common/interfaces'
import { shadowToCssFilter } from './shadowCss'
import { AdjustmentsConfig, adjustmentsToCssFilter } from './adjustCss'
import {
  PositionPadding,
  positionPaddingRatios,
  positionReframes,
  resolveEffectivePosition,
  symmetricPadding,
} from './positionFit'
import { BatchBadgeAsset, BatchBadgeConfig, drawBatchBadge } from './badge'
import { loadImageFromURL } from '@/scenes/engine/utils/image-loader'
import { processImageForCanvas } from '@/utils/imageProcessingUtils'
import { createSerialLane } from '@/utils/serialLane'
import { resetCutoutWorker, tryBuildCutoutInWorker } from './cutoutWorkerClient'

/** Bbox spanning the whole image — the Position fallback "subject" when there's no cutout to reframe. */
export const FULL_IMAGE_BBOX: NormalizedBBox = { x: 0, y: 0, width: 1, height: 1 }

export function getImageDimensions(dataUrl: string): Promise<{ width: number; height: number; aspectRatio: number }> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight, aspectRatio: img.naturalWidth / img.naturalHeight })
    img.onerror = () => resolve({ width: 0, height: 0, aspectRatio: 1 })
    img.src = dataUrl
  })
}

// Canvas-less image processing, mirroring src/services/ai/imageService.ts.
const imageProcessing = new MediaImageRepositoryProcessingDOM()

/**
 * The pass's lane for pixel work — one for the session, shared by every image.
 *
 * Background removal runs 20 requests wide because the API call is the slow part,
 * but each request's pixel stages — resizing the original for the model, scaling
 * the returned mask back up, compositing the cutout, scanning it for a bounding
 * box — are long synchronous chunks that only the main thread can run. Twenty of
 * those interleaved leave nothing for the interface: a rail click could not paint
 * its inspector until the whole pile had drained.
 */
const runBatchPixelWork = createSerialLane()

/** Bytes + intrinsic size of one imported file, for its placeholder card. */
export interface BatchImageFileData {
  originalDataUrl: string
  originalWidth: number
  originalHeight: number
  originalAspectRatio: number
}

/** Called per file by the deferred import (see `batchImport.ts`), so each card fills
 *  in independently instead of the whole selection resolving at once. */
export async function loadBatchImageFileData(file: File): Promise<BatchImageFileData> {
  // HEIC/HEIF can't render in <img>/canvas; the read transcodes those to JPEG from
  // the file's own bytes, in a worker where one can run — see HeifConverter.
  const originalDataUrl = await imageProcessing.readUploadedImageAsDataUrl(file)
  const { width, height, aspectRatio } = await getImageDimensions(originalDataUrl)
  if (!width || !height) throw new Error(`loadBatchImageFileData: could not decode ${file.name}`)
  return {
    originalDataUrl,
    originalWidth: width,
    originalHeight: height,
    originalAspectRatio: aspectRatio,
  }
}

/** Re-encodes any loadable image source as a self-contained PNG data URL.
 *  PNG (not JPEG) so alpha survives. */
async function toDurablePngDataUrl(src: string): Promise<string> {
  const img = await loadHTMLImage(src)
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0)
  return canvas.toDataURL('image/png')
}

/**
 * Normalizes one uploaded badge for fast preview/export while preserving alpha.
 *
 * Returns a DURABLE data URL. `processImageForCanvas` bounding-box-crops the
 * badge (trimming transparent margins, which we want) but hands back an object
 * URL from `URL.createObjectURL` — and a `blob:` URL is only valid for the
 * lifetime of the document that minted it. Persisting one produced a badge that
 * rendered in the session that uploaded it and then silently vanished after any
 * reload, since the stored string had become a dangling pointer. Re-encoding
 * here keeps the crop while making the bytes self-contained.
 */
export async function loadBatchBadgeFileData(file: File): Promise<string> {
  const rawDataUrl = await fileToDataUrl(file)
  const canvasReady = await imageProcessing.convertToHeicToJpegIfNeeded(rawDataUrl, file)
  const processed = await processImageForCanvas(canvasReady, { maxEdgeSize: 2048 }, imageProcessing)
  if (!processed.startsWith('blob:')) return processed
  try {
    return await toDurablePngDataUrl(processed)
  } finally {
    URL.revokeObjectURL(processed)
  }
}

export const BBOX_PADDING_RATIO = 0.1

/** A mask's subject bounding box plus the mask's own pixel dimensions. */
export interface MaskBBoxResult {
  bbox: NormalizedBBox
  width: number
  height: number
}

/**
 * Computes the subject's bounding box directly from a removal mask (WHITE = keep),
 * normalized to 0..1 fractions of the mask's own pixel dimensions — which match the
 * composited cutout's dimensions, since compositing always draws at the mask's size.
 * Scanning the mask instead of compositing a cutout first avoids doing the (more
 * expensive) full-resolution alpha composite just to measure the subject. `isMask=false`
 * here because our masks are WHITE=keep (high value = subject), the same polarity as an
 * alpha channel — no flip needed, unlike the editor's own inverted (BLACK=keep) masks.
 */
export async function computeMaskBBox(maskDataUrl: string): Promise<MaskBBoxResult | null> {
  try {
    const { rect, width, height } = await imageProcessing.extractBoundingBoxWithSize(maskDataUrl, false)
    return normalizeMaskBBox(rect, width, height)
  } catch {
    return null
  }
}

/** A bounding box in mask pixels, as the store keeps it: fractions of the mask. */
function normalizeMaskBBox(
  rect: { x: number; y: number; width: number; height: number },
  imgW: number,
  imgH: number,
): MaskBBoxResult | null {
  if (!imgW || !imgH || rect.width <= 0 || rect.height <= 0) return null
  return {
    bbox: {
      x: rect.x / imgW,
      y: rect.y / imgH,
      width: rect.width / imgW,
      height: rect.height / imgH,
    },
    width: imgW,
    height: imgH,
  }
}

/**
 * Composites a full-resolution transparent cutout from an original image and a
 * removal mask (WHITE = keep). Delegates to the app's shared GPU mask compositor
 * (`applyMaskToImage`, a fabric WebGL filter) instead of a per-pixel JS loop:
 * for a 4000×4000 image the old loop ran 64M iterations on the main thread and
 * blocked it — at bg-removal concurrency 20 that stacked into visible freezes.
 *
 * `flip: false` keeps the mask's luminance as the alpha directly (WHITE = keep),
 * matching the batch mask polarity — the filter would otherwise invert it. Full
 * pixel dimensions are preserved (the filter canvas is sized to the image), so
 * this stays a drop-in replacement with no bounding-box crop.
 */
export async function compositeCutout(originalDataUrl: string, maskDataUrl: string): Promise<string> {
  const [originalImg, maskImg] = await Promise.all([
    imageProcessing.loadImage(originalDataUrl),
    imageProcessing.loadImage(maskDataUrl),
  ])
  if (!originalImg || !maskImg) {
    throw new Error('compositeCutout: failed to load original or mask image')
  }
  return imageProcessing.applyMaskToImage(originalImg, maskImg, false)
}

/** Editor masks store SUBJECT = BLACK; batch convention is WHITE = keep. */
export async function invertMask(maskDataUrl: string): Promise<string> {
  const base64 = await imageProcessing.invertMask(maskDataUrl)
  return `data:image/png;base64,${base64}`
}

export interface RemoveBackgroundResult {
  /** Removal mask, WHITE = keep. Stored as the versioned source of truth. */
  mask: string
  /**
   * The composited cutout, as an object URL when the worker built it and a data
   * URL when the main-thread fallback did (that path ends in `canvas.toDataURL`).
   * Every consumer takes either — image `src`, `ImageJs.load`, `toBlob` — so the
   * difference stays inside this module.
   *
   * Object URLs live as long as the document unless revoked. Once in the store,
   * `batchObjectUrlRevocationMiddleware` revokes them as the slice evicts them;
   * a result dropped before it gets there is revoked by its dropper.
   */
  cutout: string
  bboxResult: MaskBBoxResult | null
}

/** Thrown by `removeBackground` when the pass was cancelled mid-flight. The queue
 *  swallows it — nobody is waiting for that image any more — but it has to leave
 *  the pipeline somehow, and a half-built result would be worse. */
export class BatchRemovalAbortedError extends Error {
  constructor() {
    super('Batch background removal was aborted')
    this.name = 'BatchRemovalAbortedError'
  }
}

function revokeRemovalResult({ mask, cutout }: RemoveBackgroundResult): void {
  for (const url of [mask, cutout]) {
    if (url.startsWith('blob:')) URL.revokeObjectURL(url)
  }
}

function throwIfAborted(abortSignal?: AbortSignal): void {
  if (abortSignal?.aborted) throw new BatchRemovalAbortedError()
}

export async function removeBackground(
  originalDataUrl: string,
  abortSignal?: AbortSignal,
): Promise<RemoveBackgroundResult> {
  const { rawMask, originalSize, targetSize } = await requestBgRemoveMask(
    originalDataUrl,
    imageProcessing,
    abortSignal,
    runBatchPixelWork,
  )
  const ratioX = originalSize.width / targetSize.width
  const ratioY = originalSize.height / targetSize.height

  // The mask the API returns is WHITE=subject (keep) — no inversion needed. Its
  // unsquashing, the cutout composite and the bbox scan are one worker job, so a
  // pass costs the main thread nothing per photo. The signal goes with it: a
  // request still queued client-side is dropped before its payload is ever copied
  // across, and `resetCutoutWorker` (below) takes care of the ones already there.
  const built = await tryBuildCutoutInWorker(
    {
      maskBase64: rawMask,
      ratioX,
      ratioY,
      originalDataUrl,
      maskThreshold: imageProcessing.maskThreshold,
    },
    abortSignal,
  )
  if (built) {
    // Both blobs go into the store as object URLs. Base64-ing the cutout instead
    // cost a full-resolution PNG encoded to a ~1.37x string on the main thread,
    // per photo, at removal concurrency 20 — allocated, held in redux, and parsed
    // again by every <img> that showed it. An object URL is a pointer to bytes the
    // worker already produced.
    return {
      mask: URL.createObjectURL(built.maskBlob),
      cutout: URL.createObjectURL(built.cutoutBlob),
      bboxResult: normalizeMaskBBox(built.rect, built.width, built.height),
    }
  }
  // A cancelled request answers `null` too — falling back here would move the work
  // we just cancelled onto the main thread, which is the one place it hurts most.
  throwIfAborted(abortSignal)

  // No worker: the same three stages on the main thread, queued on the lane rather
  // than racing each other — overlapping them won a little latency per image and
  // cost the interface its responsiveness across the pass. Each stage rechecks the
  // signal as it comes off the lane: a job queued behind twenty others would
  // otherwise still spend a chunk of main thread on a result nobody reads.
  const resizedMask = await runBatchPixelWork(() => {
    throwIfAborted(abortSignal)
    return imageProcessing.postProcessFromBazaartBg(rawMask, ratioX, ratioY)
  })
  const cutout = await runBatchPixelWork(() => {
    throwIfAborted(abortSignal)
    return compositeCutout(originalDataUrl, resizedMask)
  })
  const bboxResult = await runBatchPixelWork(() => {
    throwIfAborted(abortSignal)
    return computeMaskBBox(resizedMask)
  })
  return { mask: resizedMask, cutout, bboxResult }
}

export interface RunOnImagesItem {
  id: string
  originalDataUrl: string
  /** Content-version pointer these bytes came from; late results must still match. */
  contentVersion?: number
}

export interface RunOnImagesCallbacks {
  concurrency?: number
  abortSignal?: AbortSignal
  onItemSuccess?: (
    id: string,
    result: RemoveBackgroundResult,
    bboxResult: MaskBBoxResult | null,
    item: RunOnImagesItem,
  ) => void
  onItemError?: (id: string, error: unknown) => void
}

export interface BgRemovalQueue {
  /** Adds items to the live pool. Safe to call while it is still draining. */
  push: (items: RunOnImagesItem[]) => void
}

/**
 * Background removal over an *appendable* queue with bounded concurrency.
 *
 * A fixed item list doesn't suit a deferred import: files resolve a few at a time, so
 * each arrival would either spawn its own pool (multiplying concurrency by the number
 * of arrivals) or abort the previous wave's in-flight requests. One long-lived pool
 * holds concurrency at `concurrency` however the work trickles in, which lets removal
 * overlap the import instead of waiting for it. `abortSignal` retires the pool.
 */
export function createBgRemovalQueue({
  concurrency = 20,
  abortSignal,
  onItemSuccess,
  onItemError,
}: RunOnImagesCallbacks = {}): BgRemovalQueue {
  const queue: RunOnImagesItem[] = []
  let active = 0

  // Aborting has to reach the cutout worker as well as the request queue. Without
  // this, the cutouts already posted keep being built after the user cancels or
  // leaves the screen, and the next pass — same worker — queues behind them.
  abortSignal?.addEventListener('abort', () => resetCutoutWorker(), { once: true })

  const pump = () => {
    while (active < concurrency && queue.length) {
      if (abortSignal?.aborted) return
      const item = queue.shift()!
      active++
      void (async () => {
        try {
          const result = await removeBackground(item.originalDataUrl, abortSignal)
          if (!abortSignal?.aborted) onItemSuccess?.(item.id, result, result.bboxResult, item)
          // Nobody takes a cancelled pass's result into the store, so nothing there
          // would ever revoke its object URLs.
          else revokeRemovalResult(result)
        } catch (error) {
          if (!abortSignal?.aborted) onItemError?.(item.id, error)
        } finally {
          active--
          pump()
        }
      })()
    }
  }

  return {
    push(items: RunOnImagesItem[]) {
      if (abortSignal?.aborted || !items.length) return
      queue.push(...items)
      pump()
    },
  }
}

/** Placement of a drawn image inside a target canvas. */
interface ContainFit {
  dx: number
  dy: number
  dw: number
  dh: number
}

/**
 * Computes `object-fit: contain` placement: scale the natural size down to fit the
 * target (no upscaling beyond fit), centered. Factored out so future manual
 * drag/crop placement can bypass it.
 */
function computeContainFit(
  naturalW: number,
  naturalH: number,
  targetW: number,
  targetH: number,
): ContainFit {
  const scale = Math.min(targetW / naturalW, targetH / naturalH)
  const dw = Math.round(naturalW * scale)
  const dh = Math.round(naturalH * scale)
  const dx = Math.round((targetW - dw) / 2)
  const dy = Math.round((targetH - dh) / 2)
  return { dx, dy, dw, dh }
}

/** Source-rectangle crop for `object-fit: cover`: scale up so the target rect is
 *  fully filled, then centered-crop whichever axis overflows. Used to draw a
 *  custom background image the same way GeneratedCard renders it in CSS
 *  (`background-size: cover; background-position: center`). */
function computeCoverFit(
  naturalW: number,
  naturalH: number,
  targetW: number,
  targetH: number,
): { sx: number; sy: number; sw: number; sh: number } {
  const scale = Math.max(targetW / naturalW, targetH / naturalH)
  const sw = targetW / scale
  const sh = targetH / scale
  const sx = (naturalW - sw) / 2
  const sy = (naturalH - sh) / 2
  return { sx, sy, sw, sh }
}

function loadHTMLImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

/**
 * Shared layout for bbox-driven framing: the uniform `scale` applied to the whole
 * cutout and the unrounded top-left draw offset (`dx`, `dy`) of the scaled image.
 * The subject bbox is scaled to fill the target minus the per-side padding, then
 * placed inside that padded region (centered within any leftover slack). Equal
 * padding on all sides reduces to a centered subject. Returns null when the bbox
 * or the padded region is degenerate.
 */
function computeBBoxLayout(
  bbox: NormalizedBBox,
  naturalW: number,
  naturalH: number,
  targetW: number,
  targetH: number,
  pad: PositionPadding,
): { scale: number; dx: number; dy: number } | null {
  const bw = bbox.width * naturalW
  const bh = bbox.height * naturalH
  if (bw <= 0 || bh <= 0) return null

  const availW = targetW * (1 - pad.left - pad.right)
  const availH = targetH * (1 - pad.top - pad.bottom)
  if (availW <= 0 || availH <= 0) return null
  const scale = Math.min(availW / bw, availH / bh)

  // Position the subject bbox inside the padded region, centered within the slack
  // left over on the axis that isn't the limiting one.
  const regionX = pad.left * targetW
  const regionY = pad.top * targetH
  const subjX = regionX + (availW - bw * scale) / 2
  const subjY = regionY + (availH - bh * scale) / 2
  // Full-image top-left so the scaled bbox lands at (subjX, subjY).
  const dx = subjX - bbox.x * naturalW * scale
  const dy = subjY - bbox.y * naturalH * scale
  return { scale, dx, dy }
}

/**
 * Contain-fit driven by the subject's bounding box instead of the full image.
 * Scales the whole cutout so the bbox fills the target minus `padding` per side,
 * centered per {@link computeBBoxLayout}. Falls back to whole-image contain when
 * the bbox is degenerate. Returns the placement of the FULL image (dw/dh are the
 * scaled image size, dx/dy its top-left — which can be negative when the image
 * overflows the frame, like CSS `object-fit: cover`).
 */
function computeBBoxContainFit(
  bbox: NormalizedBBox,
  naturalW: number,
  naturalH: number,
  targetW: number,
  targetH: number,
  pad: PositionPadding,
): ContainFit {
  const layout = computeBBoxLayout(bbox, naturalW, naturalH, targetW, targetH, pad)
  if (!layout) return computeContainFit(naturalW, naturalH, targetW, targetH)
  return {
    dx: Math.round(layout.dx),
    dy: Math.round(layout.dy),
    dw: Math.round(naturalW * layout.scale),
    dh: Math.round(naturalH * layout.scale),
  }
}

/**
 * CSS-transform analog of `computeBBoxContainFit`, applied to a cutout `<img>`
 * that is already `object-fit: contain` inside a target-aspect tile. It scales
 * and repositions the subject so the rendered card matches the export exactly.
 * All quantities are ratios, so the transform is resolution-independent (percent
 * translate is relative to the element's own box) and needs no measured pixel
 * size. Returns null when a bbox frame shouldn't be applied.
 */
export function computeBBoxPreviewTransform(
  bbox: NormalizedBBox,
  naturalW: number,
  naturalH: number,
  targetW: number,
  targetH: number,
  pad: PositionPadding = symmetricPadding(BBOX_PADDING_RATIO),
): string | null {
  const layout = computeBBoxLayout(bbox, naturalW, naturalH, targetW, targetH, pad)
  if (!layout) return null
  const baseScale = Math.min(targetW / naturalW, targetH / naturalH)
  if (baseScale <= 0) return null
  const k = layout.scale / baseScale
  // Undo the object-fit:contain centering, then apply the export draw offset:
  // T = drawOffset + scaledImageCenter - tileCenter (see derivation in review).
  const txPct = (100 * (layout.dx + (naturalW / 2) * layout.scale - targetW / 2)) / targetW
  const tyPct = (100 * (layout.dy + (naturalH / 2) * layout.scale - targetH / 2)) / targetH
  return `translate(${txPct}%, ${tyPct}%) scale(${k})`
}

// Loads a source image, applies object-fit:contain centering into a canvas at the target
// size, and fills margins with bgColor / a cover-fit bgImageSrc (or white for JPEG when
// neither is set). Both renderExport and renderExportToBlob delegate here for the shared
// canvas setup. Adjustments (CSS filter) and shadow (drop-shadow) are baked into the
// drawn image via ctx.filter — combined so the export matches the card preview.
async function renderToCanvas(
  sourceDataUrl: string,
  targetWidth: number,
  targetHeight: number,
  bgColor: string | null,
  mime: string,
  shadow?: ShadowConfig | null,
  adjustments?: AdjustmentsConfig | null,
  bbox?: NormalizedBBox | null,
  pad: PositionPadding = symmetricPadding(BBOX_PADDING_RATIO),
  bgImageSrc?: string | null,
  badge?: BatchBadgeConfig | null,
  badgeAsset?: BatchBadgeAsset,
  fitMode: 'contain' | 'cover' = 'contain',
): Promise<HTMLCanvasElement> {
  const [img, bgImage] = await Promise.all([
    loadImageFromURL(sourceDataUrl) as unknown as Promise<HTMLImageElement | null>,
    bgImageSrc ? loadHTMLImage(bgImageSrc) : Promise.resolve(null),
  ])
  if (!img) throw new Error('Failed to load batch export source')
  const placement = bbox
    ? computeBBoxContainFit(
        bbox,
        img.naturalWidth,
        img.naturalHeight,
        targetWidth,
        targetHeight,
        pad,
      )
    : computeContainFit(img.naturalWidth, img.naturalHeight, targetWidth, targetHeight)
  const { dx, dy, dw, dh } = placement
  const sourceCover = fitMode === 'cover' && !bbox
  const canvas = document.createElement('canvas')
  canvas.width = targetWidth
  canvas.height = targetHeight
  const ctx = canvas.getContext('2d')!
  ctx.filter = 'none'
  if (bgImage) {
    // Cover-fit, matching GeneratedCard's CSS `background-size: cover; background-position: center`.
    const { sx, sy, sw, sh } = computeCoverFit(bgImage.naturalWidth, bgImage.naturalHeight, targetWidth, targetHeight)
    ctx.drawImage(bgImage, sx, sy, sw, sh, 0, 0, targetWidth, targetHeight)
  } else {
    const fill = bgColor ?? (mime === 'image/jpeg' ? '#ffffff' : null)
    if (fill) {
      ctx.fillStyle = fill
      ctx.fillRect(0, 0, targetWidth, targetHeight)
    }
  }
  const maxEdge = sourceCover ? Math.max(targetWidth, targetHeight) : Math.max(dw, dh)
  const drawFilter = [
    adjustmentsToCssFilter(adjustments, maxEdge),
    shadow ? shadowToCssFilter(shadow, maxEdge, true) : '',
  ]
    .filter(Boolean)
    .join(' ')
  if (drawFilter) ctx.filter = drawFilter
  if (sourceCover) {
    const { sx, sy, sw, sh } = computeCoverFit(
      img.naturalWidth,
      img.naturalHeight,
      targetWidth,
      targetHeight,
    )
    ctx.drawImage(img as unknown as CanvasImageSource, sx, sy, sw, sh, 0, 0, targetWidth, targetHeight)
  } else {
    ctx.drawImage(img as unknown as CanvasImageSource, dx, dy, dw, dh)
  }
  if (drawFilter) ctx.filter = 'none'
  await drawBatchBadge(ctx, targetWidth, targetHeight, badge, badgeAsset)
  return canvas
}

async function renderExport(
  sourceDataUrl: string,
  targetWidth: number,
  targetHeight: number,
  bgColor: string | null,
  shadow?: ShadowConfig | null,
  adjustments?: AdjustmentsConfig | null,
  bbox?: NormalizedBBox | null,
  pad?: PositionPadding,
  bgImageSrc?: string | null,
  badge?: BatchBadgeConfig | null,
  badgeAsset?: BatchBadgeAsset,
  fitMode: 'contain' | 'cover' = 'contain',
): Promise<string> {
  const canvas = await renderToCanvas(sourceDataUrl, targetWidth, targetHeight, bgColor, 'image/png', shadow, adjustments, bbox, pad, bgImageSrc, badge, badgeAsset, fitMode)
  return canvas.toDataURL('image/png')
}

async function renderExportToBlob(
  sourceDataUrl: string,
  targetWidth: number,
  targetHeight: number,
  bgColor: string | null,
  mime: string,
  quality: number,
  shadow?: ShadowConfig | null,
  adjustments?: AdjustmentsConfig | null,
  bbox?: NormalizedBBox | null,
  pad?: PositionPadding,
  bgImageSrc?: string | null,
  badge?: BatchBadgeConfig | null,
  badgeAsset?: BatchBadgeAsset,
  fitMode: 'contain' | 'cover' = 'contain',
): Promise<Blob> {
  const canvas = await renderToCanvas(sourceDataUrl, targetWidth, targetHeight, bgColor, mime, shadow, adjustments, bbox, pad, bgImageSrc, badge, badgeAsset, fitMode)
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      blob => (blob ? resolve(blob) : reject(new Error('canvas.toBlob returned null'))),
      mime,
      quality,
    )
  )
}

// Resolves the render source, background color, output dimensions, and shadow for a batch tile.
// No resize preset → default square at the larger natural dimension (no upscaling).
async function resolveExportParams(
  image: BatchImage,
): Promise<{
  source: string
  color: string | null
  bgImageSrc: string | null
  width: number
  height: number
  shadow: ShadowConfig | null
  adjustments: AdjustmentsConfig | null
  bbox: NormalizedBBox | null
  pad: PositionPadding
  badge: BatchBadgeConfig | null
  badgeAsset?: BatchBadgeAsset
  fitMode: 'contain' | 'cover'
}> {
  // Composite from the mask at full resolution; fall back to the cache only if
  // mask bytes are somehow missing.
  const mask = image.maskVersion != null ? image.masks?.[image.maskVersion] : undefined
  const usesCutout = image.bgRemoved && (!!mask || !!image.cutoutDataUrl)
  // The cutout cache (url + bbox) fills asynchronously and can lag a fresh mask version.
  // On a cache miss, recompute both from the mask here so export never uses a bbox that
  // doesn't match the cutout it's framing.
  const needsLiveBBox = usesCutout && !image.cutoutDataUrl && !!mask
  const [cutout, liveBBoxResult] = await Promise.all([
    usesCutout ? image.cutoutDataUrl ?? (mask ? compositeCutout(image.originalDataUrl, mask) : null) : null,
    needsLiveBBox ? computeMaskBBox(mask) : null,
  ])
  const source = usesCutout ? cutout! : image.originalDataUrl
  const color = usesCutout ? (image.bgColor ?? null) : null
  const bgImageSrc = usesCutout ? (image.bgImageSrc ?? null) : null
  const shadow = usesCutout ? (image.shadow ?? null) : null
  // Adjustments apply to the rendered image regardless of background removal.
  const adjustments = image.adjustments ?? null
  const badge = image.badge ?? null
  const badgeAsset = image.badgeAsset

  // Position drives the framing: Center/Custom reframe a subject bbox with the
  // per-side padding; Original leaves the full image. Independent of resize.
  // With a cutout, the subject is the mask's bbox; otherwise there's no subject
  // to isolate, so the "subject" is the whole original image (pad around it, keep
  // its own background).
  const position = resolveEffectivePosition(image)
  const pad = positionPaddingRatios(position)
  const cutoutBBox = image.cutoutBBox ?? liveBBoxResult?.bbox ?? null
  const hasCutoutBBox = usesCutout && !!cutoutBBox && cutoutBBox.width > 0 && cutoutBBox.height > 0
  const subjectBBox = hasCutoutBBox ? cutoutBBox : FULL_IMAGE_BBOX
  const bbox = positionReframes(position) ? subjectBBox : null
  const fitMode = getResizeObjectFit(image, !!bbox)

  if (image.targetWidth && image.targetHeight) {
    return { source, color, bgImageSrc, width: image.targetWidth, height: image.targetHeight, shadow, adjustments, bbox, pad, badge, badgeAsset, fitMode }
  }

  const img = await loadHTMLImage(source)
  // No resize preset => export at the source's natural size (still reframed by Position).
  return { source, color, bgImageSrc, width: img.naturalWidth, height: img.naturalHeight, shadow, adjustments, bbox, pad, badge, badgeAsset, fitMode: 'contain' }
}

/**
 * Resolves the final export data URL for a batch tile, baking in the background
 * color when the background was removed and rendering at the exact target size.
 * Shared by per-card download, the navbar "Download all", and open-in-editor so
 * they always produce identical output that matches the card preview.
 */
export async function buildExportDataUrl(image: BatchImage): Promise<string> {
  const { source, color, bgImageSrc, width, height, shadow, adjustments, bbox, pad, badge, badgeAsset, fitMode } = await resolveExportParams(image)
  return renderExport(source, width, height, color, shadow, adjustments, bbox, pad, bgImageSrc, badge, badgeAsset, fitMode)
}

function greatestCommonDivisor(a: number, b: number): number {
  let x = Math.abs(a)
  let y = Math.abs(b)
  while (y) {
    const t = y
    y = x % y
    x = t
  }
  return x || 1
}

/**
 * Re-frames a data URL so its pixel dimensions are an exact integer multiple of
 * the frame's — the only way the two ratios can be truly equal.
 *
 * Cropping to the frame's ratio as a float is not enough: a 2732x2048 frame
 * reduces to 683:512 = 1.333984, and the nearest integer size an Edit-with-AI
 * result can be cropped to (1184x888 = 1.333333) is still 0.05% off, which is a
 * visible ~1px bar at export width and a sub-pixel seam in the card. So snap to
 * k * (frame reduced by its GCD) instead, picking the k closest to the source's
 * own resolution — here 2 * 683x512 = 1366x1024, a 1.15x resample.
 *
 * Cover-crops and resamples in one draw. Returns the input untouched when it
 * already matches, or when either size is unusable.
 */
export async function fitDataUrlToFrame(
  dataUrl: string,
  frameWidth: number,
  frameHeight: number,
): Promise<string> {
  if (!(frameWidth > 0) || !(frameHeight > 0)) return dataUrl
  const img = await loadHTMLImage(dataUrl)
  const sourceWidth = img.naturalWidth
  const sourceHeight = img.naturalHeight
  if (!sourceWidth || !sourceHeight) return dataUrl

  const divisor = greatestCommonDivisor(frameWidth, frameHeight)
  const unitWidth = frameWidth / divisor
  const unitHeight = frameHeight / divisor
  // Never larger than the frame itself, and never smaller than one unit.
  const maxSteps = Math.max(1, Math.floor(Math.min(frameWidth / unitWidth, frameHeight / unitHeight)))
  const steps = Math.min(
    maxSteps,
    Math.max(1, Math.round(Math.min(sourceWidth / unitWidth, sourceHeight / unitHeight))),
  )
  const width = unitWidth * steps
  const height = unitHeight * steps
  if (width === sourceWidth && height === sourceHeight) return dataUrl

  const { sx, sy, sw, sh } = computeCoverFit(sourceWidth, sourceHeight, width, height)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')!
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height)
  return canvas.toDataURL('image/png')
}

/**
 * The composite Edit-with-AI works from: everything the card shows except the
 * badge. A badge is a corner sticker the export stamps on last, not part of the
 * photo — baking it into the generation input has the model redraw, warp or
 * caption it. The tile keeps its badge, so the generated result still gets one
 * stamped at its own export.
 *
 * Also what the prompt box previews, so the thumbnail matches what is sent.
 */
function buildAiCompositionDataUrl(
  image: BatchImage,
  options: { fitTargetFrame?: boolean } = {},
): Promise<string> {
  return buildExportDataUrl({
    ...image,
    ...(options.fitTargetFrame ? { resizeMode: 'fit' as const } : {}),
    badge: null,
    badgeAsset: undefined,
  })
}

export function buildEditWithAiSourceDataUrl(image: BatchImage): Promise<string> {
  return buildAiCompositionDataUrl(image)
}

/**
 * Expand starts from the same user-visible composition as Edit with AI, but its
 * empty target-frame area must remain transparent so the transient Flux mask can
 * identify exactly what to synthesize. The badge stays live and is re-applied
 * after generation instead of being redrawn by the model.
 */
export function buildExpandSourceDataUrl(image: BatchImage): Promise<string> {
  return buildAiCompositionDataUrl(image, { fitTargetFrame: true })
}

/**
 * Blob-native alternative to buildExportDataUrl + convertImagesToFormat + base64ToBlob.
 * Renders directly to a Blob in the target format, skipping two base64 encode/decode cycles.
 * Use this for ZIP and direct saves; fall back to buildExportDataUrl only for PDF (jsPDF needs data URLs).
 */
export async function buildExportBlob(
  image: BatchImage,
  mime: string,
  quality: number,
): Promise<Blob> {
  const { source, color, bgImageSrc, width, height, shadow, adjustments, bbox, pad, badge, badgeAsset, fitMode } = await resolveExportParams(image)
  return renderExportToBlob(source, width, height, color, mime, quality, shadow, adjustments, bbox, pad, bgImageSrc, badge, badgeAsset, fitMode)
}
